/**
 * ================================================================================================
 *  משימות על תיק הלקוח — the company-scoped create, which had a route and no starting point
 * ================================================================================================
 *
 * `companies.work_item.create` has existed since §6.11 and `reachability.ts` carried it with this
 * reason:
 *
 *     *"What is missing is the SURFACE: an item about one client file is raised from the company
 *     screen, which this round did not touch. The route is right and the caller is later; a thin
 *     caller added here to keep the gap list flat is what section 8 forbids."*
 *
 * This is the caller, on the screen that paragraph names.
 *
 * ── WHY IT IS NOT THE TASKS SCREEN'S FORM WITH A COMPANY FIELD ────────────────────────────────
 *
 * Because the route is not the tenant one with an extra argument. The company is in the PATH, and
 * the gate's stage 4 resolves it — asserting the explicit grant, the access level, the company's
 * lifecycle and that it belongs to this office — BEFORE the handler runs. A tenant-scoped form
 * with a company picker would make the product resolve a company itself, which is a second answer
 * to *"may this caller see that company"*, and the gate is the first.
 *
 * Here the company is the screen you are standing on, so there is no picker and nothing to choose
 * wrongly.
 *
 * ── IT LISTS NOTHING ──────────────────────────────────────────────────────────────────────────
 *
 * `tenant.work_item.list` takes a SCOPE, and none of its scopes is *"items about this company"*.
 * So this panel raises an item and says where to read it, rather than rendering a filtered list
 * the server was never asked for — a list assembled on the client from a different question is
 * how a count becomes wrong.
 */

import { useState, type FormEvent } from 'react';

import { ActionFailureNotice } from './ActionFailureNotice';
import { callOperation } from '../api/operations';
import { classifyActionError } from '../api/actionOutcome';
import type { ActionFailure } from '../api/actionOutcome';
import { routes } from '../routes';

export function CompanyWorkItems({ companyUid }: { readonly companyUid: string }): JSX.Element {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [dueOn, setDueOn] = useState('');
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<ActionFailure | null>(null);
  const [raised, setRaised] = useState<string | null>(null);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (sending || title.trim() === '') return;
    setSending(true);
    setFailure(null);
    try {
      await callOperation('companies.work_item.create', {
        params: { company_uid: companyUid },
        body: {
          title: title.trim(),
          /*
           * `null` AND NOT `''`. An empty string is a body of no characters and no body is a
           * different fact; the column is nullable for that reason, and the tenant-scoped form
           * makes the same distinction in the same words.
           */
          body: body.trim() === '' ? null : body.trim(),
          dueOn: dueOn === '' ? null : dueOn,
        },
      });
      setRaised(title.trim());
      setTitle('');
      setBody('');
      setDueOn('');
    } catch (err) {
      setFailure(classifyActionError(err));
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="panel" data-area="company-work-items">
      <h2 className="panel__title">משימה על התיק הזה</h2>
      <p className="notice__body">
        משימה שנפתחת כאן משויכת לתיק הלקוח שעל המסך. היא מופיעה ברשימת המשימות של הארגון, ולא ברשימה
        נפרדת של התיק.
      </p>

      {failure === null ? null : (
        <ActionFailureNotice failure={failure} onStepUpSatisfied={() => setFailure(null)} />
      )}
      {raised === null ? null : (
        <p className="notice__body" data-area="company-work-item-raised">
          {`המשימה „${raised}” נפתחה. `}
          <a href={routes.tenantWorkItems}>מעבר למשימות הארגון</a>
        </p>
      )}

      <form className="form form--inline" onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span className="field__label">נושא</span>
          <input
            className="field__input"
            name="company-work-item-title"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
          />
        </label>
        <label className="field">
          <span className="field__label">פירוט</span>
          <textarea
            className="field__input"
            name="company-work-item-body"
            value={body}
            onChange={(e) => setBody(e.currentTarget.value)}
          />
        </label>
        <label className="field">
          <span className="field__label">לביצוע עד</span>
          <input
            className="field__input"
            name="company-work-item-due"
            type="date"
            value={dueOn}
            onChange={(e) => setDueOn(e.currentTarget.value)}
          />
          {/* A DAY, not a moment. A filing deadline is a day, and a time would invent a cut-off. */}
          <span className="field__hint">יום, בלי שעה.</span>
        </label>
        <button type="submit" className="btn btn--primary" disabled={sending || title.trim() === ''}>
          {sending ? 'פותח…' : 'פתיחת משימה'}
        </button>
      </form>
    </section>
  );
}
